export interface SelectionChange {
  selected: string[];
  primary: string | null;
}

export type SelectionListener = (change: SelectionChange) => void;

/**
 * Tracks the set of selected flora instance ids (spec §3.6). Selection is canonical editor state,
 * decoupled from the Three.js scene; the {@link SelectionRenderer} reflects it visually. Supports
 * single and additive (multi) selection so Milestone 4 can extend it without a rewrite.
 */
export class SelectionManager {
  private readonly selectedIds = new Set<string>();
  private primaryId: string | null = null;
  private readonly listeners = new Set<SelectionListener>();

  get count(): number {
    return this.selectedIds.size;
  }

  get primary(): string | null {
    return this.primaryId;
  }

  isEmpty(): boolean {
    return this.selectedIds.size === 0;
  }

  isSelected(id: string): boolean {
    return this.selectedIds.has(id);
  }

  selected(): string[] {
    return [...this.selectedIds];
  }

  onChange(listener: SelectionListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Select an id. When `additive` is false, replaces the current selection. */
  select(id: string, additive = false): void {
    if (!additive) {
      this.selectedIds.clear();
    }
    this.selectedIds.add(id);
    this.primaryId = id;
    this.emit();
  }

  /** Select exactly this set of ids (primary = last). */
  selectOnly(ids: Iterable<string>): void {
    this.selectedIds.clear();
    let last: string | null = null;
    for (const id of ids) {
      this.selectedIds.add(id);
      last = id;
    }
    this.primaryId = last;
    this.emit();
  }

  deselect(id: string): void {
    if (!this.selectedIds.delete(id)) return;
    if (this.primaryId === id) {
      this.primaryId = this.lastOf(this.selectedIds);
    }
    this.emit();
  }

  /** Toggle an id's membership; becomes primary when added. */
  toggle(id: string): void {
    if (this.selectedIds.has(id)) {
      this.deselect(id);
    } else {
      this.select(id, true);
    }
  }

  clear(): void {
    if (this.selectedIds.size === 0 && this.primaryId === null) return;
    this.selectedIds.clear();
    this.primaryId = null;
    this.emit();
  }

  /** Drop ids that no longer exist (e.g., after a delete). */
  prune(exists: (id: string) => boolean): void {
    let changed = false;
    for (const id of [...this.selectedIds]) {
      if (!exists(id)) {
        this.selectedIds.delete(id);
        changed = true;
      }
    }
    if (this.primaryId && !exists(this.primaryId)) {
      this.primaryId = this.lastOf(this.selectedIds);
      changed = true;
    }
    if (changed) this.emit();
  }

  private lastOf(set: Set<string>): string | null {
    let last: string | null = null;
    for (const id of set) last = id;
    return last;
  }

  private emit(): void {
    const change: SelectionChange = { selected: [...this.selectedIds], primary: this.primaryId };
    for (const listener of this.listeners) {
      listener(change);
    }
  }
}
